// webapp/src/components/slotPickerHelpers.ts
//
// Phase 31.6 Plan 03 — FE-31-11 (react-refresh/only-export-components).
//
// `dispatchPackPostConfirm` lived inside SlotPicker.tsx and was exported for
// unit testing, which tripped react-refresh/only-export-components (a file that
// exports a component must not also export non-component values). Moving the
// pure helper + its structural client type to this sibling module clears the
// lint finding while keeping the unit-test surface identical (the test now
// imports it from here).
//
// Refs: phase 31.6

import { invokeAuthed, type AuthedInvokeClient } from '../lib/invokeAuthed'

// -----------------------------------------------------------------------------
// Slot shape + bookability predicate (Phase 89 — lifted out of SlotPicker.tsx).
//
// SlotPicker.tsx may export ONLY the component (react-refresh/only-export-components,
// the reason this sibling module exists), so the shared slot type + predicate +
// multi-hour derivation live here where the derivation can reuse slotBookable as
// the single source of truth for is_taken + the coaching 48h gate.
// -----------------------------------------------------------------------------

export interface AvailabilitySlot {
  event_type_slug: string
  slot_starts_at: string
  slot_ends_at: string
  is_taken: boolean
}

// Phase 74 (COACH-01): coaching-perso enforces a server-side ≥48h lead time —
// reserve_slot rejects any slot with slot_starts_at < now()+48h (slot_too_soon).
// Mirror that boundary client-side so <48h slots are never shown/clickable for
// coaching. The boundary matches the server exactly (a slot at now()+48h is
// bookable). For every other offer the lead-time check is a no-op.
export const COACHING_MIN_LEAD_MS = 48 * 60 * 60 * 1000

export function slotBookable(s: AvailabilitySlot, isCoachingPerso: boolean): boolean {
  if (s.is_taken) return false
  if (isCoachingPerso && new Date(s.slot_starts_at).getTime() < Date.now() + COACHING_MIN_LEAD_MS) {
    return false
  }
  return true
}

/**
 * Phase 89 — pure derivation of the bookable multi-hour START times.
 *
 * A start S is bookable at `durationMin` (60/120/180) ⟺ the N = durationMin/60
 * hourly sub-slots {S, S+60min, …, S+(N-1)·60min} ALL exist in `slots` AND ALL
 * pass slotBookable (not taken; coaching 48h gate). This mirrors the server:
 * reserve_slot rejects any booking overlapping the elongated span, and its
 * span-open guard requires every hourly sub-slot to exist (89-CONTEXT §2.3).
 *
 * durationMin=60 reproduces the current single-slot bookable set (no 1h
 * regression). Sub-slot lookup is by exact timestamp (S + k·60min) via a Map
 * keyed on the start instant, tolerant of the 10-min-step overlapping list.
 */
export function bookableMultiHourStarts(
  slots: AvailabilitySlot[],
  durationMin: number,
  isCoachingPerso: boolean,
): AvailabilitySlot[] {
  const n = Math.max(1, Math.round(durationMin / 60))
  const byStart = new Map<number, AvailabilitySlot>()
  for (const s of slots) byStart.set(new Date(s.slot_starts_at).getTime(), s)

  return slots.filter((start) => {
    const t0 = new Date(start.slot_starts_at).getTime()
    for (let k = 0; k < n; k++) {
      const sub = byStart.get(t0 + k * 60 * 60_000)
      if (!sub || !slotBookable(sub, isCoachingPerso)) return false
    }
    return true
  })
}

/**
 * Structural client surface used by `dispatchPackPostConfirm`. Re-exported from
 * the shared `invokeAuthed` helper: the call now needs `auth.getSession` (to
 * attach the user JWT) in addition to `functions.invoke`. Lets us inject a
 * `vi.fn()` stub in unit tests without importing the real client.
 */
export type SupabaseFunctionsClient = AuthedInvokeClient

/**
 * Pure helper — exported for unit testing — that fires the post-confirm
 * side-effect EF (`notify-pack-booking`) after a pack booking is confirmed
 * in the DB. The EF orchestrates SwitchBot keypad programming + Brevo email.
 *
 * Goes through `invokeAuthed` so the logged-in user's JWT is explicitly attached
 * — without it, supabase-js intermittently forwards an anon/stale token and the
 * EF returns 401 (no PIN programmed, no email). See `lib/invokeAuthed.ts`.
 *
 * Best-effort semantics: the booking is already confirmed at this point, so
 * we never throw. Errors and unexpected exceptions are logged to the console
 * and swallowed so the caller can always navigate to the confirmation page.
 * (The server-side cron backstop is the real guarantee the email/PIN ship.)
 */
export async function dispatchPackPostConfirm(
  client: SupabaseFunctionsClient,
  bookingId: string,
): Promise<void> {
  try {
    const { error: notifyErr } = await invokeAuthed(client, 'notify-pack-booking', {
      body: { booking_id: bookingId },
    })
    if (notifyErr) {
      console.warn('notify-pack-booking failed (booking still confirmed)', notifyErr)
    }
  } catch (e) {
    console.warn('notify-pack-booking threw (booking still confirmed)', e)
  }
}
